/**
 * One Gemini Live session that narrates the comparison and returns its own
 * transcript.
 *
 * Audio arrives as raw PCM and every event is stamped with the byte count
 * received so far, which is an exact offset into the generated track — that is
 * what gives captions their word timing and the character its cues, without any
 * dependence on wall-clock or network jitter.
 *
 * The pose tools are still declared — the calls add beats the words can't
 * reveal — but they cannot be made mandatory (Live sessions have no
 * `toolConfig`), so `timing.ts` always derives the choreography from the
 * transcript and treats the calls as extra.
 */
import {
	ActivityHandling,
	GoogleGenAI,
	Modality,
	type FunctionCall,
	type FunctionResponse,
	type LiveServerMessage,
	type Session,
} from "@google/genai";
import { PcmAccumulator } from "./pcm";
import {
	buildKickoff,
	buildPoseTools,
	buildSystemInstruction,
	OPTION_ARG,
} from "./prompt";
import { buildPoseSegments, OPENING_HOLD_MS, type PoseEvent } from "./timing";
import { buildCaptionLines, buildWordTimeline } from "./words";
import type {
	ComparisonSpec,
	NarrationResult,
	PoseKind,
	TranscriptDelta,
} from "./types";

const DEFAULT_MODEL = "gemini-3.1-flash-live-preview";

/** Give up if nothing has arrived for this long. */
const SILENCE_TIMEOUT_MS = 25_000;
/** Hard ceiling on one generation, well inside the 15-minute session limit. */
const MAX_SESSION_MS = 180_000;
/** Less than this much audio means the model never really started. */
const MIN_AUDIO_MS = 1_500;
/**
 * Audio at the head with no transcript over it is only believable as narration
 * for so long. Past this it is a whole extra pass, not a lagging transcript.
 */
const UNTRANSCRIBED_HEAD_MS = 5_000;

/**
 * Which pose a call asks for.
 *
 * Pointing carries the option in an argument rather than in the function name,
 * so resolving it means matching that argument back to a label. The value is an
 * enum of the two labels and normally comes back verbatim, but the model can
 * still answer with a near-miss, hence the fallback: the longer match wins,
 * because one label is so often a substring of the other (Git/GitHub,
 * Java/JavaScript). Scoring "GitHub" as a hit for both would point the wrong
 * way half the time.
 */
function poseFromCall({
	call,
	spec,
}: {
	call: FunctionCall;
	spec: ComparisonSpec;
}): PoseKind | null {
	if (call.name === "strikeThinkingPose") return "thinking";
	if (call.name === "lowerBothArms") return "idle";
	if (call.name !== "pointAtOption") return null;

	const option = normalize({ text: String(call.args?.[OPTION_ARG] ?? "") });
	if (!option) return null;

	const thisLabel = normalize({ text: spec.thisLabel });
	const thatLabel = normalize({ text: spec.thatLabel });
	if (option === thisLabel) return "this";
	if (option === thatLabel) return "that";

	// The instruction also introduces them as "Option A" and "Option B", and the
	// model sometimes answers with the letter instead of the label. Checked
	// after the labels, so a option genuinely named "A" still wins.
	if (option === "a" || option === "option a") return "this";
	if (option === "b" || option === "option b") return "that";

	const thisScore = option.includes(thisLabel) ? thisLabel.length : 0;
	const thatScore = option.includes(thatLabel) ? thatLabel.length : 0;
	if (thisScore === thatScore) return null;
	return thisScore > thatScore ? "this" : "that";
}

function normalize({ text }: { text: string }): string {
	return text
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, "")
		.trim();
}

export interface GenerateNarrationOptions {
	token: string;
	model?: string;
	spec: ComparisonSpec;
	onProgress?: ({ secondsGenerated }: { secondsGenerated: number }) => void;
}

export async function generateNarration({
	token,
	model,
	spec,
	onProgress,
}: GenerateNarrationOptions): Promise<NarrationResult> {
	const ai = new GoogleGenAI({
		apiKey: token,
		httpOptions: { apiVersion: "v1alpha" },
	});

	// The narration is padded with silence up front so the video can open on the
	// idle character. Because the padding is inside the accumulator, every cue
	// below is stamped past it without any of them knowing.
	const pcm = new PcmAccumulator({ leadInMs: OPENING_HOLD_MS });
	/** The call id rides along so a `toolCallCancellation` can retract the cue. */
	const toolEvents: Array<PoseEvent & { id?: string }> = [];
	const deltas: TranscriptDelta[] = [];

	let session: Session | null = null;
	// A tool call that lands before `connect()` resolves would otherwise be
	// dropped — and because function calling is synchronous here, a dropped
	// response hangs the model forever. Buffer and flush instead.
	const pendingToolResponses: FunctionResponse[] = [];
	let settled = false;
	let silenceTimer: ReturnType<typeof setTimeout> | null = null;
	let hardTimer: ReturnType<typeof setTimeout> | null = null;
	let lastProgressAt = 0;

	return new Promise<NarrationResult>((resolve, reject) => {
		const cleanup = () => {
			if (silenceTimer) clearTimeout(silenceTimer);
			if (hardTimer) clearTimeout(hardTimer);
			silenceTimer = null;
			hardTimer = null;
			try {
				session?.close();
			} catch {
				/* already closed */
			}
		};

		const fail = ({ message }: { message: string }) => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(new Error(message));
		};

		/**
		 * Throw away a long opening stretch of audio that no transcript covers.
		 *
		 * The API transcribes what the model *says*, so narration and transcript
		 * arrive together. A run of audio at the head with nothing over it is
		 * therefore not narration at all — it is a pass that never counted as the
		 * answer, whether that's reasoning spoken aloud or a take the server
		 * replaced without saying so. Keeping it is what produced a video of
		 * double the requested length that said everything twice, the first copy
		 * with no captions and no pose cues.
		 *
		 * The threshold is deliberately far longer than any plausible lag in the
		 * transcript stream, so a normal run never trims a syllable.
		 */
		const dropUntranscribedHead = () => {
			const firstDeltaMs = deltas[0]?.atMs;
			if (firstDeltaMs === undefined) return;
			if (firstDeltaMs - OPENING_HOLD_MS <= UNTRANSCRIBED_HEAD_MS) return;

			const droppedMs = pcm.dropAudioBefore({ atMs: firstDeltaMs });
			if (droppedMs <= 0) return;

			// Everything stamped against the old track moves back with it. Cues
			// that end up inside the lead-in are floored by `timing.ts`.
			for (const delta of deltas) delta.atMs -= droppedMs;
			for (const event of toolEvents) event.atMs -= droppedMs;
		};

		const finish = () => {
			if (settled) return;
			dropUntranscribedHead();

			// Deliberately `audioMs`, not `elapsedMs`: the lead-in silence is not
			// evidence that the model narrated anything.
			if (pcm.audioMs < MIN_AUDIO_MS) {
				fail({
					message:
						"The model didn't produce a narration. Try rephrasing the topic and generate again.",
				});
				return;
			}

			settled = true;
			cleanup();

			const durationMs = pcm.elapsedMs;
			const words = buildWordTimeline({ deltas, durationMs });
			const { segments, toolCuesUsed } = buildPoseSegments({
				toolEvents,
				words,
				thisLabel: spec.thisLabel,
				thatLabel: spec.thatLabel,
				durationMs,
			});

			resolve({
				audioBlob: pcm.toWavBlob(),
				durationMs,
				poses: segments,
				captions: buildCaptionLines({ words }),
				transcript: words.map((word) => word.text).join(" "),
				toolCuesUsed,
			});
		};

		const armSilenceTimer = () => {
			if (silenceTimer) clearTimeout(silenceTimer);
			silenceTimer = setTimeout(() => {
				// Silence after real audio just means the turn ended without a
				// `turnComplete` reaching us — keep what we have.
				if (pcm.audioMs >= MIN_AUDIO_MS) finish();
				else fail({ message: "The AI session timed out before it said anything." });
			}, SILENCE_TIMEOUT_MS);
		};

		const reportProgress = () => {
			if (!onProgress) return;
			const now = Date.now();
			if (now - lastProgressAt < 250) return;
			lastProgressAt = now;
			onProgress({ secondsGenerated: pcm.durationSeconds });
		};

		/** Returns false when the send failed and the run was already failed out. */
		const flushToolResponses = (): boolean => {
			if (!session || pendingToolResponses.length === 0) return true;
			const functionResponses = pendingToolResponses.splice(0);
			try {
				session.sendToolResponse({ functionResponses });
				return true;
			} catch (error) {
				fail({
					message:
						error instanceof Error
							? error.message
							: "The AI session dropped mid-generation.",
				});
				return false;
			}
		};

		const handleMessage = (message: LiveServerMessage) => {
			// A message can still land between resolving and the socket closing;
			// past that point the buffers have already been read.
			if (settled) return;
			armSilenceTimer();

			const content = message.serverContent;

			// The server cancelled that generation and will start over. Everything
			// accumulated belongs to the discarded take, and the server throws away
			// its transcript and tool calls too — so keeping the audio produced a
			// file with the narration twice, the first copy with no captions and no
			// pose cues. Drop it all and let the retake refill the buffers.
			if (content?.interrupted) {
				pcm.reset();
				deltas.length = 0;
				toolEvents.length = 0;
				pendingToolResponses.length = 0;
				// Deliberately ignore the rest of this message, `turnComplete`
				// included: it describes the take that was just thrown away.
				return;
			}

			// Calls belonging to a cancelled generation must not become cues.
			const cancelledIds = message.toolCallCancellation?.ids ?? [];
			if (cancelledIds.length > 0) {
				const cancelled = new Set(cancelledIds);
				for (let i = toolEvents.length - 1; i >= 0; i--) {
					const id = toolEvents[i].id;
					if (id && cancelled.has(id)) toolEvents.splice(i, 1);
				}
			}

			// Gemini 3.x packs several parts into one event (audio + transcript
			// together), so every branch below runs — none of them are exclusive.
			for (const part of content?.modelTurn?.parts ?? []) {
				// A thought part is the model reasoning, not its answer. In an
				// audio-only session there is no text channel for it to reason in,
				// so it comes back as speech — speech that
				// `outputAudioTranscription` does not cover, because it isn't
				// output. Banking it puts an unsubtitled rehearsal of the script in
				// front of the real narration.
				if (part.thought) continue;
				const data = part.inlineData?.data;
				if (data) {
					pcm.push({ base64: data });
					reportProgress();
				}
			}

			const transcript = content?.outputTranscription?.text;
			if (transcript) {
				deltas.push({ text: transcript, atMs: pcm.elapsedMs });
			}

			const functionCalls = message.toolCall?.functionCalls ?? [];
			if (functionCalls.length > 0) {
				for (const call of functionCalls) {
					const pose = poseFromCall({ call, spec });
					// The call arrives before the audio for the sentence it belongs
					// to exists, so the current byte position IS the cut point.
					if (pose) toolEvents.push({ pose, atMs: pcm.elapsedMs, id: call.id });
					pendingToolResponses.push({
						id: call.id,
						name: call.name,
						response: { result: "ok" },
					});
				}
				if (!flushToolResponses()) return;
			}

			if (content?.turnComplete) finish();
		};

		(async () => {
			try {
				session = await ai.live.connect({
					model: model || DEFAULT_MODEL,
					callbacks: {
						onopen: () => armSilenceTimer(),
						onmessage: handleMessage,
						onerror: (event: ErrorEvent) => {
							fail({
								message:
									event?.message || "The AI session failed to connect.",
							});
						},
						onclose: (event: CloseEvent) => {
							// A close after usable audio is a normal end of turn.
							if (pcm.audioMs >= MIN_AUDIO_MS) finish();
							else
								fail({
									message:
										event?.reason || "The AI session closed unexpectedly.",
								});
						},
					},
					config: {
						// These two must match what /api/agent/token locked into the
						// ephemeral token, or the server rejects the setup.
						responseModalities: [Modality.AUDIO],
						sessionResumption: {},
						outputAudioTranscription: {},
						// Barge-in is the default: any activity signal cuts the model
						// off mid-answer and it starts the take again. That is designed
						// for a live conversation — here there is no user to interrupt,
						// only one scripted monologue, and a cut take is pure corruption.
						realtimeInputConfig: {
							activityHandling: ActivityHandling.NO_INTERRUPTION,
						},
						speechConfig: {
							voiceConfig: {
								prebuiltVoiceConfig: { voiceName: spec.voiceName },
							},
						},
						systemInstruction: buildSystemInstruction({ spec }),
						tools: [{ functionDeclarations: buildPoseTools({ spec }) }],
						// No thinkingConfig on purpose — Live's MINIMAL default stands.
						// Raising it to LOW was a nudge towards calling the pose tools,
						// and the duplicated-narration bug arrived with it: an
						// audio-only model has nowhere to think except out loud, so the
						// deliberation was spoken, untranscribed, ahead of the answer.
						// Tool calls are only additive here (timing.ts derives the
						// choreography from the transcript), so that trade was never
						// worth a video of twice the requested length.
					},
				});

				hardTimer = setTimeout(() => {
					if (pcm.audioMs >= MIN_AUDIO_MS) finish();
					else fail({ message: "The AI session ran too long. Try a shorter topic." });
				}, MAX_SESSION_MS);

				armSilenceTimer();
				flushToolResponses();
				// Gemini 3.x only accepts sendClientContent for seeding history, so
				// the kickoff turn goes through the realtime channel.
				session.sendRealtimeInput({ text: buildKickoff({ spec }) });
			} catch (error) {
				fail({
					message:
						error instanceof Error
							? error.message
							: "Could not start the AI session.",
				});
			}
		})();
	});
}
